import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { setStyleProperty as _$setStyleProperty } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { use as _$use } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<div class=killcam-frame-layer>`),
  _tmpl$2 = /*#__PURE__*/_$template(`<div class=killcam-frame data-testid=killcam-frame><div class=killcam-frame__bar data-testid=killcam-frame-bar><span class=killcam-frame__tag>KILL CAM</span><button type=button class=killcam-frame__close data-testid=killcam-frame-close aria-label="Close the kill camera for this sortie"title="Close for this sortie — PREFERENCES (O) turns KILL CAMERA off for good">×</button></div><div class=killcam-frame__resize data-testid=killcam-frame-resize>`);
import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { clampKillCamPlacement, movedKillCamPlacement, placementOfRect, resizedKillCamPlacement, resolveKillCamRect } from "./killcam-layout.ts";

/**
 * The kill camera's window chrome: a title bar to drag it by, a close control
 * on that bar, and a corner handle to resize it (user request, 2026-08-26).
 *
 * **The picture is not in here.** It is a second scissored render pass inside
 * the WebGPU canvas (`scene-killcam.ts`); this component draws the one-pixel
 * border round the outside of it and nothing else. The two agree because both
 * ask `resolveKillCamRect` for the rectangle, with the same placement, against
 * boxes that are the same box — the canvas host and this layer are both
 * `inset: 0` in `.flight`. That agreement used to be maintained by writing
 * `KILL_CAM_LAYOUT`'s numbers out a second time as CSS percentages; now that
 * the window moves, it is maintained by measurement.
 *
 * ## Why the frame body still ignores the pointer
 *
 * `.killcam-frame` keeps `pointer-events: none` and only the bar, the close
 * button and the resize handle take `auto`. Holding the primary button is the
 * trigger (`pointer.ts`), and a pilot who fires through the corner of his own
 * screen while a wreck is falling there should still be firing — the window is
 * an inset picture, not a panel over the cockpit. The three controls are the
 * exception, and `keys.ts`'s `UI_OVERLAY_CLASSES` carries `killcam-frame` so a
 * click on any of them cannot fall through and pull the trigger.
 *
 * ## Live drag, committed release
 *
 * A pointer move calls `onMove` and a release calls `onCommit`. The split is
 * not cosmetic: `onMove` runs at the pointer's rate and must reach the render
 * pass so the picture travels with its frame, while `onCommit` is what writes
 * `localStorage` — a JSON serialisation per pointer event would be a synchronous
 * string write at 120 Hz inside the flight loop.
 */
export function KillCamFrame(props) {
  let layer;
  const [viewWidth, setViewWidth] = createSignal(0);
  const [viewHeight, setViewHeight] = createSignal(0);
  function measure(element) {
    setViewWidth(element.clientWidth);
    setViewHeight(element.clientHeight);
  }
  onMount(() => {
    const element = layer;
    if (!element) return;
    measure(element);
    // The re-clamp on a resolution change: the rectangle below is recomputed
    // from the new box, and `resolveKillCamRect` clamps every time it is asked.
    const observer = new ResizeObserver(() => measure(element));
    observer.observe(element);
    onCleanup(() => observer.disconnect());
  });
  const rect = () => {
    const width = viewWidth();
    const height = viewHeight();
    if (width === 0 || height === 0) return null;
    return resolveKillCamRect(props.placement, width, height);
  };
  function startGesture(event, kind) {
    // Primary button only: the secondary one is free look (`free-look-input.ts`).
    if (event.button !== 0) return;
    const start = rect();
    if (!start) return;
    // No native drag, and no trigger pull behind it.
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    const origin = placementOfRect(start);
    const startX = event.clientX;
    const startY = event.clientY;
    handle.setPointerCapture(event.pointerId);
    const placementAt = moved => {
      const dx = moved.clientX - startX;
      const dy = moved.clientY - startY;
      return kind === "move" ? movedKillCamPlacement(origin, dx, dy) : resizedKillCamPlacement(origin, dx, dy);
    };
    const onPointerMove = moved => props.onMove(placementAt(moved));
    const onPointerEnd = ended => {
      handle.removeEventListener("pointermove", onPointerMove);
      handle.removeEventListener("pointerup", onPointerEnd);
      handle.removeEventListener("pointercancel", onPointerEnd);
      // Store where the window *ended up*, not where the pointer went: a drag
      // pushed against an edge is clamped on the way to the screen, and storing
      // the unclamped value would put the difference back the moment the view
      // grew again.
      const settled = placementAt(ended);
      const landed = clampKillCamPlacement(settled, viewWidth(), viewHeight());
      props.onCommit(landed ? placementOfRect(landed) : settled);
    };
    handle.addEventListener("pointermove", onPointerMove);
    handle.addEventListener("pointerup", onPointerEnd);
    handle.addEventListener("pointercancel", onPointerEnd);
  }
  return (() => {
    var _el$ = _tmpl$();
    var _ref$ = layer;
    typeof _ref$ === "function" ? _$use(_ref$, _el$) : layer = _el$;
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return rect();
      },
      children: box => (() => {
        var _el$2 = _tmpl$2(),
          _el$3 = _el$2.firstChild,
          _el$4 = _el$3.firstChild,
          _el$5 = _el$4.nextSibling,
          _el$6 = _el$3.nextSibling;
        _el$3.$$pointerdown = event => startGesture(event, "move");
        _el$5.$$click = () => props.onClose();
        _el$5.$$pointerdown = event => event.stopPropagation();
        _el$6.$$pointerdown = event => startGesture(event, "resize");
        _$effect(_p$ => {
          var _v$ = `${box().x}px`,
            _v$2 = `${box().y}px`,
            _v$3 = `${box().width}px`,
            _v$4 = `${box().height}px`;
          _v$ !== _p$.e && _$setStyleProperty(_el$2, "left", _p$.e = _v$);
          _v$2 !== _p$.t && _$setStyleProperty(_el$2, "top", _p$.t = _v$2);
          _v$3 !== _p$.a && _$setStyleProperty(_el$2, "width", _p$.a = _v$3);
          _v$4 !== _p$.o && _$setStyleProperty(_el$2, "height", _p$.o = _v$4);
          return _p$;
        }, {
          e: undefined,
          t: undefined,
          a: undefined,
          o: undefined
        });
        return _el$2;
      })()
    }));
    return _el$;
  })();
}
_$delegateEvents(["pointerdown", "click"]);